import { useCallback, useMemo } from 'react'

const normalizeId = id => String(id)

const uniqById = list => {
	const seen = new Set()
	const out = []
	for (const item of list || []) {
		const key = normalizeId(item?.id)
		if (!key || seen.has(key)) continue
		seen.add(key)
		out.push(item)
	}
	return out
}

export const useFiltersSortBar = ({
	baseItems = [],
	activeIds = [],
	onChange,
}) => {
	const safeBase = useMemo(() => uniqById(baseItems), [baseItems])

	const hasAnyNonAllActive = useMemo(() => {
		return (activeIds || []).some(id => normalizeId(id) !== 'all')
	}, [activeIds])

	const computedActiveIds = useMemo(() => {
		if (!hasAnyNonAllActive) return ['all']
		return (activeIds || []).filter(id => normalizeId(id) !== 'all')
	}, [activeIds, hasAnyNonAllActive])

	const isActive = useCallback(
		id => computedActiveIds.includes(normalizeId(id)),
		[computedActiveIds]
	)

	const getItemById = useCallback(
		id => safeBase.find(x => normalizeId(x.id) === normalizeId(id)),
		[safeBase]
	)

	const toggle = useCallback(
		id => {
			const key = normalizeId(id)
			const item = getItemById(key)

			if (key === 'all') {
				onChange?.({
					activeIds: ['all'],
					lastToggledId: 'all',
					isActiveNow: true,
					item,
				})
				return
			}

			const prev = computedActiveIds.map(normalizeId)
			const wasActive = prev.includes(key)

			const next = wasActive ? prev.filter(x => x !== key) : [...prev, key]
			const finalIds = next.length ? next : ['all']

			onChange?.({
				activeIds: finalIds,
				lastToggledId: key,
				isActiveNow: !wasActive,
				item,
			})
		},
		[computedActiveIds, getItemById, onChange]
	)

	const handleClickBase = useCallback(
		item => {
			if (!item) return
			toggle(item.id)
		},
		[toggle]
	)

	return {
		baseItems: safeBase,
		activeIds: computedActiveIds,
		isActive,
		handleClickBase,
	}
}
